import React, { useCallback, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import Button from 'portal-modules/common/components/Button'
import { useTransition } from 'react-spring'
import { Modal } from '@entrata/portals-common/lib/components'
import {
  portalReskinStyle,
  rootStyle,
  StyledModalDiv,
  StyledWrapper,
} from './rp-modal'
import styled from '@emotion/styled'
import {
  underlineFocus,
  FocusSpan,
} from 'portal-modules/common/components/focus-styles'
import { mediaQueries } from 'portal-modules/tenant-portal-layout/constants/responsive-breakpoints'

const MODAL_WIDTH = '600px'
const MODAL_RADIUS = '24px'
const MODAL_MOBILE = mediaQueries.mobile

const ModalStack = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: stretch;
  gap: 8px;
  width: ${MODAL_WIDTH};
  height: 100vh;
  ${MODAL_MOBILE} {
    width: 100%;
    height: 100%;
  }
`

const ModStyledModalDiv = styled(StyledModalDiv)`
  position: absolute;
  width: ${MODAL_WIDTH};
  height: 100vh;
  background: ${({ theme }) => theme.mode.reskinBackground};
  backdrop-filter: blur(20px);
  box-shadow: 0 1px 10px rgba(0, 0, 0, 0.1);
  border-radius: ${MODAL_RADIUS};
  &.remove-pad {
    padding-bottom: 0;
  }

  ${ModalStack} > & {
    position: relative;
    width: 100%;
    height: auto;
    max-height: calc(100vh - 56px);
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  ${MODAL_MOBILE} {
    width: 100%;
    border-radius: ${MODAL_RADIUS} ${MODAL_RADIUS} 0 0;
    margin: 0;
    height: auto;

    ${ModalStack} > & {
      height: auto;
      max-height: none;
      flex: 1 1 0px;
      min-height: 0;
    }
  }
`

const ModStyledWrapper = styled(StyledWrapper)`
  display: flex;
  width: ${MODAL_WIDTH};
  height: 100vh;
  margin: 0;

  ${MODAL_MOBILE} {
    width: 100%;
    height: calc(100vh - 48px);
  }
`

const ImageWrapper = styled('div')`
  width: 100%;
  height: 280px;
  border-radius: ${MODAL_RADIUS} ${MODAL_RADIUS} 0 0;
  background-image: ${({ image_url }) => `url(${image_url})`};
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  ${MODAL_MOBILE} {
    width: 100%;
  }
`

const HeadingTitle = styled('h3')`
  font-family: ${({ theme }) => theme.fonts.body};
  font-weight: 700;
  font-size: 21px;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.button_secondary.color};
  margin: 40px 48px 24px 46px;
  display: flex;
  justify-content: flex-start;
  flex-shrink: 0;
  ${MODAL_MOBILE} {
    margin: 24px 16px 24px 16px;
  }
`

const SubHeadingTitle = styled('h6')`
  font-family: ${({ theme }) => theme.fonts.body};
  font-weight: 700;
  font-size: 16px;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.button_secondary.color};
  height: 21px;
  margin: 0 48px 16px 46px;
  display: flex;
  justify-content: flex-start;
  ${MODAL_MOBILE} {
    margin: 0 16px 24px 16px;
  }
`

const HeadingDescription = styled('p')`
  font-weight: 400;
  font-size: 13px;
  letter-spacing: 0.25px;
  color: ${({ theme }) => theme.gray.mediumlight};
  margin: 0 48px;
  height: ${({ image_url }) => (image_url ? '341px' : '620px')};
  overflow-y: auto;
  overflow-x: auto;
  ${MODAL_MOBILE} {
    margin: 0 16px;
    height: auto;
    flex: 1 1 0px;
    min-height: 0;
  }
`

const ButtonWrapper = styled('div')`
  display: flex;
  justify-content: center;
  margin: 24px 0 40px 0;
  flex-shrink: 0;
  ${MODAL_MOBILE} {
    margin-left: 18px;
    margin-bottom: 24px;
  }
`

const BannerCancel = styled('button')`
  flex-shrink: 0;
  width: 100%;
  box-sizing: border-box;
  background: none;
  border: none;
  color: ${({ theme }) => theme.text.white};
  cursor: pointer;
  display: block;
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: 1rem;
  font-weight: bold;
  margin: 0;
  padding: 16px;
  text-align: center;

  ${underlineFocus}
`

const ModalPanel = ({
  hideCloseButton,
  containerProps,
  titleId,
  image_url,
  title,
  subtitle,
  content,
  closeButtonType,
  closeButtonLabel,
  saveNoMarginRight,
  onClose,
}) => (
  <ModStyledModalDiv
    className={!hideCloseButton ? 'remove-pad' : ''}
    {...containerProps}
  >
    {image_url && <ImageWrapper image_url={image_url} />}
    <HeadingTitle id={titleId}>{title}</HeadingTitle>
    {subtitle && <SubHeadingTitle>{subtitle}</SubHeadingTitle>}
    <HeadingDescription image_url={image_url}>{content}</HeadingDescription>
    <ButtonWrapper>
      <Button
        appearance={closeButtonType}
        size='medium'
        noMarginRight={saveNoMarginRight}
        onClick={onClose}
      >
        {__(closeButtonLabel)}
      </Button>
    </ButtonWrapper>
  </ModStyledModalDiv>
)

ModalPanel.propTypes = {
  hideCloseButton: PropTypes.bool,
  containerProps: PropTypes.object,
  titleId: PropTypes.string,
  image_url: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  content: PropTypes.node,
  closeButtonType: PropTypes.string,
  closeButtonLabel: PropTypes.string,
  saveNoMarginRight: PropTypes.bool,
  onClose: PropTypes.func,
}

const RpCookiesModal = ({
  blurId,
  hideCloseButton,
  onClose,
  onCancel,
  image_url,
  rootId,
  show,
  title,
  subtitle,
  content,
  closeButtonType,
  closeButtonLabel,
  cancelButtonLabel,
}) => {
  const rootRef = useRef(null)
  const blurRef = useRef(null)

  const toggleRoot = useCallback(
    action => {
      if (!rootRef.current) rootRef.current = document.getElementById(rootId)
      if (!blurRef.current) blurRef.current = document.getElementById(blurId)
      rootRef.current?.classList[action](rootStyle)
      blurRef.current?.classList[action]('blur')
    },
    [rootId, blurId],
  )

  useEffect(() => {
    toggleRoot(show ? 'add' : 'remove')
    return () => toggleRoot('remove')
  }, [show, toggleRoot])

  const transitions = useTransition(show, null, {
    from: { opacity: 0, transform: 'translateY(100px)' },
    enter: { opacity: 1, transform: 'translateY(0px)' },
    leave: { opacity: 0, transform: 'translateY(-200px)' },
  })

  const showBannerCancel = typeof onCancel === 'function'

  const panelProps = {
    hideCloseButton,
    image_url,
    title,
    subtitle,
    content,
    closeButtonType,
    closeButtonLabel,
    onClose,
  }

  return transitions.map(
    ({ item, key, props }) =>
      item && (
        <Modal className={portalReskinStyle()} key={key} onClose={onClose}>
          {({ containerProps, titleId }) => (
            <ModStyledWrapper style={{ ...props }}>
              {showBannerCancel ? (
                <ModalStack data-testid='modal-stack'>
                  <ModalPanel
                    {...panelProps}
                    containerProps={containerProps}
                    titleId={titleId}
                    saveNoMarginRight
                  />
                  <BannerCancel
                    type='button'
                    onClick={onCancel}
                  >
                    <FocusSpan>{__(cancelButtonLabel)}</FocusSpan>
                  </BannerCancel>
                </ModalStack>
              ) : (
                <ModalPanel
                  {...panelProps}
                  containerProps={containerProps}
                  titleId={titleId}
                  saveNoMarginRight={false}
                />
              )}
            </ModStyledWrapper>
          )}
        </Modal>
      ),
  )
}

RpCookiesModal.propTypes = {
  blurId: PropTypes.string,
  hideCloseButton: PropTypes.bool,
  image_url: PropTypes.string,
  onCancel: PropTypes.func,
  cancelButtonLabel: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  rootId: PropTypes.string,
  show: PropTypes.bool,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  content: PropTypes.node,
  closeButtonType: PropTypes.string,
  closeButtonLabel: PropTypes.string,
}

RpCookiesModal.defaultProps = {
  blurId: 'bg-img',
  rootId: 'root',
  subtitle: '',
  closeButtonType: 'secondary',
  closeButtonLabel: __('Close'),
  cancelButtonLabel: __('Cancel'),
}

export default RpCookiesModal
